import React from 'react';
import { Modal, View, Text, Pressable, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ChargerAttributes } from '../../models/charger-model';

interface Props {
    visible: boolean;
    charger: ChargerAttributes | null; // Will always be null until terms accepted (kept for API compatibility)
    onReviewTerms: () => void;
    onDismiss: () => void;
}

const PartnerInvitationNotification: React.FC<Props> = ({ visible, charger, onReviewTerms, onDismiss }) => {
    // Charger will always be null until terms are accepted (API returns 403)
    // No need for conditional logic - always show account-level terms message
    
    return (
        <Modal
            visible={visible}
            animationType="slide"
            transparent={true}
        >
            <View style={styles.modalOverlay}>
                <View style={styles.modalContainer}>
                    {/* Celebration Icon */}
                    <View style={styles.iconContainer}>
                        <Ionicons name="people" size={40} color="#FFB800" />
                    </View>

                    {/* Title */}
                    <Text style={styles.title}>🎉 Welcome, Partner!</Text>

                    {/* Message */}
                    <Text style={styles.message}>
                        You've been invited to participate in MeanderEV's Revenue Share as a Designated Partner to someone hosting a charger on the MeanderEV network. This allows you to earn revenue from EV charging stations.
                    </Text>

                    <Text style={styles.subMessage}>
                        To start earning, please review and accept the Split Partner Terms and Conditions. This is a one-time agreement that applies to all Revenue-Share Chargers on your account.
                    </Text>

                    {/* Action Buttons */}
                    <View style={styles.actionButtons}>
                        <Pressable
                            style={styles.secondaryButton}
                            onPress={onDismiss}
                        >
                            <Text style={styles.secondaryButtonText}>Dismiss</Text>
                        </Pressable>

                        <Pressable
                            style={styles.primaryButton}
                            onPress={onReviewTerms}
                        >
                            <Ionicons name="document-text-outline" size={18} color="#FFFFFF" />
                            <Text style={styles.primaryButtonText}>Review & Accept Terms</Text>
                        </Pressable>
                    </View>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    modalContainer: {
        width: '100%',
        maxWidth: 500,
        backgroundColor: '#1A1D23',
        borderRadius: 16,
        padding: 24,
        alignItems: 'center',
    },
    iconContainer: {
        width: 70,
        height: 70,
        borderRadius: 35,
        backgroundColor: 'rgba(255, 184, 0, 0.1)',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 16,
    },
    title: {
        fontSize: 20,
        fontWeight: '700',
        color: '#FFFFFF',
        textAlign: 'center',
        marginBottom: 20,
        lineHeight: 28,
    },
    message: {
        fontSize: 15,
        color: '#FFFFFF',
        textAlign: 'center',
        marginBottom: 12,
        lineHeight: 22,
    },
    subMessage: {
        fontSize: 14,
        color: '#8D8E90',
        textAlign: 'center',
        marginBottom: 24,
        lineHeight: 20,
    },
    actionButtons: {
        width: '100%',
        gap: 12,
    },
    secondaryButton: {
        paddingVertical: 12,
        paddingHorizontal: 20,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#2A2D35',
        alignItems: 'center',
    },
    secondaryButtonText: {
        color: '#8D8E90',
        fontSize: 14,
        fontWeight: '600',
    },
    primaryButton: {
        flexDirection: 'row',
        backgroundColor: '#39BEB7',
        paddingVertical: 12,
        paddingHorizontal: 20,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
    },
    primaryButtonText: {
        color: '#FFFFFF',
        fontSize: 14,
        fontWeight: '700',
    },
});

export default PartnerInvitationNotification;

